import { useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    MenuItem,
    Radio,
    RadioGroup
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as Yup from 'yup';

import ControlledInput from '../forms/ControlledInput';
import ControlledSelect from '../forms/ControlledSelect';
import { StyledLoadingButton } from '../forms/LoadingButton';
import { UserRoles } from '../roster/types';
import { Session, SessionTypes } from './types';

const StyledForm = styled.form`
    margin-top: 5px;
`;

const StyledFormLabel = styled(FormLabel)`
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
`;

const IndentedDiv = styled.div`
    margin-left: 1rem;
`;

type SettingsDialogProps<T> = DialogProps & {
    session: T | null;
    open: boolean;
    loading?: boolean;
    saving?: boolean;
    onClose: () => void;
    onSave?: (data: any) => void;
    onSaveAndLaunch?: (data: any) => void;
};

type SessionInfo = Partial<Session>;

export const SettingsDialog = <T extends SessionInfo>(props: SettingsDialogProps<T>) => {
    const { t } = useTranslation('whiteboard');
    const { session, open, loading, saving, onClose, onSave, onSaveAndLaunch } = props;

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                name: Yup.string().required(t('forms.name.required'))
            }),
        [t]
    );

    const { control, handleSubmit, reset, watch } = useForm<Partial<Session>>({
        defaultValues: session || {
            name: '',
            type: SessionTypes.Shared,
            page_write_access: UserRoles.Assistant,
            page_lock_access: UserRoles.Assistant,
            presentation_access: UserRoles.Assistant
        },
        resolver: yupResolver(validationSchema)
    });

    const type = watch('type');

    useEffect(() => {
        reset(session as SessionInfo);
    }, [reset, session]);

    const formRendered = (
        <StyledForm>
            <FormControl>
                <ControlledInput name="name" label={t('name')} autoComplete="off" control={control} />
            </FormControl>

            {!session?.session_id && (
                <FormControl fullWidth>
                    <StyledFormLabel>{t('type')}</StyledFormLabel>

                    <ControlledSelect name="type" defaultValue={SessionTypes.Shared} control={control}>
                        <MenuItem value={SessionTypes.Shared}>{t('sharedWhiteboard')}</MenuItem>
                        <MenuItem value={SessionTypes.Individual}>{t('individualWhiteboards')}</MenuItem>
                    </ControlledSelect>
                </FormControl>
            )}

            {/* Page access settings are only relevant to shared sessions. Individual sessions disallow students from navigating pages. */}
            {type === SessionTypes.Shared && (
                <FormGroup>
                    <StyledFormLabel>{t('pages')}</StyledFormLabel>

                    <IndentedDiv>
                        <Controller
                            name="page_write_access"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={field.value === UserRoles.Student}
                                            onChange={(event) => field.onChange(event.target.checked ? UserRoles.Student : UserRoles.Assistant)}
                                        />
                                    }
                                    label={t('students-can-manage-pages') as string}
                                />
                            )}
                        />
                    </IndentedDiv>
                    <IndentedDiv>
                        <Controller
                            name="page_lock_access"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={field.value === UserRoles.Student}
                                            onChange={(event) => field.onChange(event.target.checked ? UserRoles.Student : UserRoles.Assistant)}
                                        />
                                    }
                                    label={t('students-can-lock-pages') as string}
                                />
                            )}
                        />
                    </IndentedDiv>
                </FormGroup>
            )}

            <FormGroup>
                <StyledFormLabel>{t('presenterMode')}</StyledFormLabel>

                <IndentedDiv>
                    <Controller
                        rules={{ required: true }}
                        control={control}
                        name="presentation_access"
                        render={({ field }) => (
                            <RadioGroup {...field}>
                                <FormControlLabel value={UserRoles.Student} control={<Radio />} label={t('everyoneCanPresent') as string} />
                                <FormControlLabel value={UserRoles.Assistant} control={<Radio />} label={t('only-teachers-can-present') as string} />
                            </RadioGroup>
                        )}
                    />
                </IndentedDiv>
            </FormGroup>
        </StyledForm>
    );

    const loadingRendered = (
        <Grid container justifyContent="center" data-testid="settings-dialog-loader">
            <Grid item>
                <CircularProgress />
            </Grid>
        </Grid>
    );

    return (
        <Dialog open={open} onClose={onClose} keepMounted maxWidth="sm" fullWidth data-testid="whiteboard-settings-dialog" aria-busy={loading}>
            <DialogTitle>{t('whiteboardSettings')}</DialogTitle>
            <DialogContent>{loading ? loadingRendered : formRendered}</DialogContent>
            <DialogActions>
                <Button disabled={saving} variant="outlined" color="primary" onClick={onClose}>
                    {t('cancel')}
                </Button>
                {onSave && (
                    <StyledLoadingButton
                        disabled={saving}
                        loading={saving}
                        variant={saving || onSaveAndLaunch ? 'outlined' : 'contained'}
                        color="primary"
                        onClick={handleSubmit((data) => onSave(data))}
                    >
                        {t('save')}
                    </StyledLoadingButton>
                )}
                {onSaveAndLaunch && (
                    <StyledLoadingButton
                        disabled={saving}
                        loading={saving}
                        variant={saving ? 'outlined' : 'contained'}
                        color="primary"
                        onClick={handleSubmit((data) => onSaveAndLaunch(data))}
                    >
                        {t('saveAndLaunch')}
                    </StyledLoadingButton>
                )}
            </DialogActions>
        </Dialog>
    );
};
